import { z } from 'zod';

// =============================================================================
// Payor Types
// =============================================================================

export const payorTypeSchema = z.enum([
  'commercial',
  'medicare_advantage',
  'medicare',
  'medicaid',
  'government',
  'ipa_network',
  'dme',
  'pbm',
  'tpa',
  'self_pay',
  'provider',
  'other',
  'unknown',
]);

export type PayorTypeValue = z.infer<typeof payorTypeSchema>;

// =============================================================================
// Payor response
// =============================================================================

export const payorResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  type: z.string(),
  stediPayerId: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  needsReview: z.boolean(),
  skip: z.boolean(),
  createdAt: z.coerce.string(),
  updatedAt: z.coerce.string(),
});

export type PayorResponse = z.infer<typeof payorResponseSchema>;

// =============================================================================
// Payor Plan response
// =============================================================================

export const payorPlanResponseSchema = z.object({
  id: z.string().uuid(),
  payorId: z.string().uuid(),
  name: z.string(),
  planType: z.string(),
  rawVariants: z.array(z.string()).nullable().optional(),
  authRequired: z.boolean().nullable().optional(),
  notes: z.string().nullable().optional(),
  needsReview: z.boolean(),
  createdAt: z.coerce.string(),
  updatedAt: z.coerce.string(),
});

export type PayorPlanResponse = z.infer<typeof payorPlanResponseSchema>;

export const payorPlanWithPayorNameSchema = payorPlanResponseSchema.extend({
  payorName: z.string(),
});

export type PayorPlanWithPayorName = z.infer<typeof payorPlanWithPayorNameSchema>;

// =============================================================================
// Accepted Payor response
// =============================================================================

export const acceptedPayorResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  payorId: z.string().uuid(),
  payorPlanId: z.string().uuid().nullable().optional(),
  authRequired: z.boolean(),
  notes: z.string().nullable().optional(),
  rowStatus: z.number(),
  createdAt: z.coerce.string(),
  updatedAt: z.coerce.string(),
});

export type AcceptedPayorResponse = z.infer<typeof acceptedPayorResponseSchema>;

export const acceptedPayorWithNamesSchema = acceptedPayorResponseSchema.extend({
  payorName: z.string(),
  planName: z.string().nullable(),
});

export type AcceptedPayorWithNames = z.infer<typeof acceptedPayorWithNamesSchema>;

// =============================================================================
// Request schemas
// =============================================================================

export const addAcceptedPayorSchema = z.object({
  payorId: z.string().uuid(),
  payorPlanId: z.string().uuid().optional(),
  authRequired: z.boolean().optional(),
  notes: z.string().optional(),
});

export type AddAcceptedPayorRequest = z.infer<typeof addAcceptedPayorSchema>;

export const updateAcceptedPayorSchema = z.object({
  authRequired: z.boolean().optional(),
  notes: z.string().optional(),
});

export type UpdateAcceptedPayorRequest = z.infer<typeof updateAcceptedPayorSchema>;

export const matchPayorSchema = z.object({
  insuranceText: z.string().min(1),
});

export type MatchPayorRequest = z.infer<typeof matchPayorSchema>;

// =============================================================================
// Match result
// =============================================================================

export const payorMatchResultSchema = z.object({
  payorId: z.string().uuid().nullable(),
  payorName: z.string().nullable(),
  payorPlanId: z.string().uuid().nullable(),
  payorPlanName: z.string().nullable(),
  confidence: z.enum(['exact', 'fuzzy', 'none']),
  matchMethod: z.enum(['variant', 'plan_name', 'payor_name']).nullable(),
  accepted: z.boolean().nullable(),
  authRequired: z.boolean().nullable(),
});

export type PayorMatchResultResponse = z.infer<typeof payorMatchResultSchema>;

// =============================================================================
// Import result
// =============================================================================

export const payorImportResultSchema = z.object({
  payors: z.number(),
  plans: z.number(),
});

export type PayorImportResult = z.infer<typeof payorImportResultSchema>;
